'use client';

import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { Input, Button, BetaFlag, ErrorState } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faUpload,
  faCaretDown,
  faList,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { useAuth } from 'src/context/auth';
import { useGetManagedUsersQuery as useGetManagedUsers } from 'src/api/redux';
import { useUploadListMutation } from 'src/api/redux/lists/listsApi';
import { PageHeader } from 'src/components/Settings/components';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';

interface ProviderData {
  'Provider Ids': string;
}

interface FormState {
  name: string;
  user_id: string;
  list: File | null;
}

interface FormErrors {
  name?: string;
  user_id?: string;
  list?: string;
}

interface ErrorStateProps {
  title: string;
  description: React.ReactNode;
}

const ListUpload: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [formState, setFormState] = useState<FormState>({
    name: '',
    user_id: '',
    list: null,
  });
  const [preview, setPreview] = useState<ProviderData[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [errorState, setErrorState] = useState<ErrorStateProps | null>(null);

  const queryParams = getSearchQueryParamsForAPI({ q: searchTerm });
  const { data: users } = useGetManagedUsers({
    companyId: user?.company?.id?.toString() || '',
    userId: user?.id?.toString() || '',
    queryParams,
  });

  const [uploadList] = useUploadListMutation();

  const selectedUser = useMemo(() => {
    return users?.results?.find((u) => u.id.toString() === formState.user_id);
  }, [users?.results, formState.user_id]);

  // Set current user as default owner if they exist in the managed users list
  useEffect(() => {
    if (users?.results && user?.id && !formState.user_id) {
      const currentUserInList = users.results.find(
        (u) => u.id.toString() === user.id.toString()
      );
      if (currentUserInList) {
        setFormState((prev) => ({
          ...prev,
          user_id: currentUserInList.id.toString(),
        }));
      }
    }
  }, [users?.results, user?.id, formState.user_id]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      setIsOpen(true);

      // Clear the selected user when searching
      if (value) {
        setFormState((prev) => ({ ...prev, user_id: '' }));
      }
    },
    []
  );

  const handleSelectUser = useCallback(
    (userId: string) => {
      const selected = users?.results?.find((u) => u.id.toString() === userId);
      if (selected) {
        setFormState((prev) => ({ ...prev, user_id: userId }));
        setSearchTerm('');
        setIsOpen(false);
      }
    },
    [users?.results]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formState.name.trim()) {
      newErrors.name = 'List name is required';
    }

    if (!formState.user_id) {
      newErrors.user_id = 'Please select a user';
    }

    if (!formState.list) {
      newErrors.list = 'Please upload a file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  const processFile = useCallback((selectedFile: File) => {
    // Reset states
    setErrors((prev) => ({ ...prev, list: undefined }));

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      setErrors((prev) => ({ ...prev, list: 'Please upload a CSV file' }));
      return;
    }

    setFormState((prev) => ({ ...prev, list: selectedFile }));

    // Read and preview the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n');

        // Check if header is correct
        if (lines[0]?.trim().toLowerCase() !== 'provider ids') {
          setErrors((prev) => ({
            ...prev,
            list: 'CSV must have "Provider Ids" as the header',
          }));
          return;
        }

        // Parse the data
        const data = lines
          .slice(1)
          .filter((line) => line.trim())
          .map((line) => ({
            'Provider Ids': line.trim(),
          }));

        setPreview(data);
      } catch (err) {
        setErrors((prev) => ({ ...prev, list: 'Error reading file' }));
      }
    };
    reader.readAsText(selectedFile);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        processFile(droppedFile);
      }
    },
    [processFile]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (!selectedFile) return;
      processFile(selectedFile);
    },
    [processFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormState((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormState({
      name: '',
      user_id: '',
      list: null,
    });
    setPreview([]);
    setErrors({});
    setErrorState(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!validateForm()) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', formState.name);
      formData.append('user_id', formState.user_id);
      formData.append('list', formState.list as File);

      await uploadList({ data: formData }).unwrap();

      resetForm();
    } catch (err) {
      const errorMessage =
        err?.data?.error_message ||
        err?.error_message ||
        err?.message ||
        'Failed to upload list';
      const isInvalidIds = /invalid provider/i.test(errorMessage);
      setErrorState({
        title: 'Upload failure',
        description: isInvalidIds ? (
          <span>
            <span className="font-semibold">
              CCNs must be 6 digits and NPIs must be 10 digits.
            </span>
            <br />
            <br />
            {errorMessage}
          </span>
        ) : (
          errorMessage
        ),
      });
    } finally {
      setIsUploading(false);
    }
  }, [formState, validateForm, resetForm, uploadList]);

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-3">
      <PageHeader>
        <div className="flex items-center gap-2">
          <Icon icon={faList} className="text-neutral-500 text-sm" />
          <h2 className="text-sm font-semibold text-primary">List Uploader</h2>
          <BetaFlag />
        </div>
        <p className="text-xs text-primary font-semibold">
          Upload a list of provider IDs to a user.
        </p>
      </PageHeader>
      <div className="bg-white shadow-sm flex flex-col border border-neutral-100 rounded-lg p-4">
        <div className="flex flex-col gap-3">
          <div className="flex gap-3 w-full">
            <div className="flex flex-col gap-1 flex-1">
              <label
                htmlFor="provider-list-name"
                className="text-xs text-primary"
                id="provider-list-name-label"
              >
                Name
              </label>
              <Input
                size="sm"
                id="provider-list-name"
                name="name"
                value={formState.name}
                onChange={handleInputChange}
                placeholder="Enter List Name..."
                className="rounded-full"
                variant="outlined"
                aria-labelledby="provider-list-name-label"
                aria-required="true"
                error={!!errors.name}
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name}</p>
              )}
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label
                htmlFor="user-select"
                className="text-xs text-primary"
                id="user-select-label"
              >
                Owner
              </label>
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Input
                    size="sm"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder={
                      selectedUser
                        ? `${selectedUser.first_name} ${selectedUser.last_name}`
                        : 'Search users...'
                    }
                    className="rounded-full"
                    variant="outlined"
                    onClick={() => setIsOpen(true)}
                    error={!!errors.user_id}
                    endDecorator={
                      <Icon icon={faCaretDown} className="text-neutral-400" />
                    }
                  />
                </div>
                {isOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg">
                    <div className="max-h-60 overflow-y-auto">
                      {users?.results?.length > 0 ? (
                        [...(users?.results || [])]
                          .sort((a, b) => {
                            return a.first_name.localeCompare(b.first_name);
                          })
                          .map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelectUser(user.id.toString());
                              }}
                              className={`w-full px-3 py-2 text-sm text-left hover:bg-neutral-50 transition-colors ${
                                formState.user_id === user.id.toString()
                                  ? 'bg-sky-50 text-sky-600'
                                  : 'text-neutral-700'
                              }`}
                            >
                              {`${user.first_name} ${user.last_name}`}
                            </button>
                          ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-neutral-500">
                          No users found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.user_id && (
                <p className="text-xs text-red-500">{errors.user_id}</p>
              )}
            </div>
          </div>
          <div
            className={`border-2 border-dashed rounded-lg p-3 w-full
                ${
                  isDragging
                    ? 'border-sky-500'
                    : 'border-neutral-200 hover:border-sky-300'
                }
                ${errors.list ? 'border-red-500' : ''}
                transition-all duration-300 ease-in-out
                cursor-pointer
                flex items-center justify-center
                `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="File upload area"
          >
            <div className="flex flex-col items-center gap-2 w-full">
              <Icon
                icon={faCloudArrowUp}
                className={`text-2xl transition-colors duration-300 ease-in-out ${
                  isDragging ? 'text-sky-500' : 'text-neutral-200'
                }`}
              />
              <p className="text-xs text-neutral-500">
                Drag and drop your CSV file here or click to upload
              </p>
              <div className="p-2 bg-neutral-50 rounded-md border border-neutral-200">
                <p className="text-xs text-neutral-500 mb-1">
                  Example CSV Format:
                </p>
                <pre className="text-xs text-neutral-600 bg-white p-1.5 rounded border border-neutral-200 overflow-x-auto">
                  Provider Ids, 123456789, 987654321, 456789123,
                </pre>
              </div>
              <Input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-upload-input"
                id="file-upload"
              />
              <div
                className="inline-flex items-center justify-center gap-2 px-4 
                  py-2 bg-neutral-50 text-neutral-700 rounded-md 
                  cursor-pointer hover:bg-neutral-100 transition-colors duration-300 ease-in-out"
              >
                <Icon icon={faUpload} className="text-neutral-500" size="sm" />
                <span className="text-xs">Choose CSV File</span>
              </div>
              <p className="text-xs text-neutral-500">
                Supported file types: CSV
              </p>
              {errors.list && (
                <p
                  className="text-xs text-red-500 transition-opacity duration-300 ease-in-out"
                  data-testid="file-error"
                >
                  {errors.list}
                </p>
              )}
              <div
                className={`w-full transition-all duration-300 ease-in-out ${
                  formState.list
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-2 h-0'
                }`}
              >
                {formState.list && !errors.list ? (
                  <div
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg w-full ${
                      errorState
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-sky-50 border border-sky-200'
                    }`}
                    data-testid="file-preview"
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        icon={errorState ? faTriangleExclamation : faCloudArrowUp}
                        className={`transition-colors duration-300 ease-in-out ${
                          errorState ? 'text-red-500' : 'text-sky-500'
                        }`}
                      />
                      <p
                        className={`text-sm font-medium ${
                          errorState ? 'text-red-600' : 'text-sky-600'
                        }`}
                      >
                        {errorState ? 'File Error' : 'File Selected'}
                      </p>
                    </div>
                    <p
                      className={`text-sm ${
                        errorState ? 'text-red-600' : 'text-sky-600'
                      }`}
                    >
                      {formState.list.name}
                    </p>
                    {!errorState && preview.length > 0 && (
                      <div className="text-xs text-sky-600 bg-sky-50 rounded-full font-normal px-2 py-0.5 transition-colors duration-300 ease-in-out">
                        {preview.length} IDs were found in the file
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex justify-end w-full gap-2">
            <Button
              variant="outlined"
              color="neutral"
              className="rounded-full w-40"
              onClick={resetForm}
              disabled={isUploading}
            >
              Reset
            </Button>
            <Button
              variant="solid"
              color="primary"
              loading={isUploading}
              onClick={handleUpload}
              disabled={
                !formState.name ||
                !formState.user_id ||
                !formState.list ||
                isUploading
              }
              className="rounded-full w-40"
            >
              Upload
            </Button>
          </div>
        </div>
      </div>
      {errorState && (
        <div className="animate-fade-in">
          <ErrorState
            size="sm"
            title={errorState.title}
            description={errorState.description}
          />
        </div>
      )}
    </div>
  );
};

export default ListUpload;
